// Phase B Step 1.2 (2026-08-22). fetchShifts()/fetchTimesheets() in the
// three date-ranged report components had no try/catch, so a rejected query
// (most concretely: FAILED_PRECONDITION for a missing composite index, the
// exact failure ReportShiftSummary hit in prod) rendered identically to a
// legitimately empty result set — "No data to display" either way. This maps
// the Firebase JS SDK's FirestoreError.code to copy a manager can act on,
// instead of the raw SDK string.
/**
 * @param {{ code?: string, message?: string }|null|undefined} err
 * @returns {string}
 */
export function describeReportQueryError(err) {
  const code = err?.code || '';
  if (code === 'failed-precondition') {
    return "This report needs a database index that hasn't been created yet. Contact support so it can be added.";
  }
  if (code === 'permission-denied') {
    return "You don't have permission to view this report.";
  }
  return 'Something went wrong generating this report. Please try again.';
}

// ---------------------------------------------------------------------------
// Phase 2 (2026-08-25, silent read failures).
//
// Live listeners need the same "map a FirestoreError code to something a person
// can act on" job the reports needed, so this lives beside describeReportQueryError
// rather than in a second module — one place that knows what Firestore codes mean.
// The copy differs because the situations differ: a report is a thing you ran and
// can re-run, while the roster is the page you are standing on.
//
// The TERMINAL/TRANSIENT split is not a style choice, it is measured behaviour.
// src/tests/rules/onSnapshotErrorBehaviour.rules.test.js drives a denied listener
// against the real emulator and shows that onSnapshot's error callback fires
// exactly once and the listener then receives NOTHING further — it does not
// retry and does not resume. So for a terminal code, "keep showing the last good
// data" means showing data that can never refresh again, silently, forever. That
// is the failure mode this phase exists to remove, so terminal failures clear.
//
// Transient codes are the opposite: the SDK handles offline itself (serving cache
// rather than erroring), so if `unavailable` does surface, throwing away a
// manager's roster mid-service over a blip would be its own outage.

/** Firestore codes where retrying or waiting cannot help — access is the problem. */
const TERMINAL_CODES = ['permission-denied', 'unauthenticated'];

/**
 * Classify a FirestoreError for a LIVE data read (listener or one-shot).
 *
 * @param {{ code?: string, message?: string }|null|undefined} err
 * @returns {{ code: string, terminal: boolean, title: string, message: string, action: string|null }}
 */
export function classifyDataError(err) {
  const code = err?.code || 'unknown';

  if (code === 'permission-denied') {
    return {
      code,
      terminal: true,
      title: 'You no longer have access',
      message: "Your access to this organisation's data has been removed, so this page can't be loaded. Anything already on screen may be out of date.",
      action: 'Sign out',
    };
  }

  if (code === 'unauthenticated') {
    return {
      code,
      terminal: true,
      title: 'Your session has expired',
      message: 'You have been signed out, so this page can no longer load data. Sign in again to continue.',
      action: 'Sign in again',
    };
  }

  if (code === 'failed-precondition') {
    return {
      code,
      terminal: true,
      title: "This data can't be loaded",
      message: "This page needs a database index that hasn't been created yet. Contact support so it can be added.",
      action: null,
    };
  }

  return {
    code,
    terminal: false,
    title: "Couldn't refresh",
    message: "We couldn't get the latest data. What you're seeing may be out of date.",
    action: null,
  };
}

/** True when the code means retrying/waiting cannot help. Exported for tests. */
export function isTerminalDataError(err) {
  return TERMINAL_CODES.includes(err?.code) || err?.code === 'failed-precondition';
}
